import { AUTH_STORAGE_KEYS } from './auth-keys'

/**
 * Übernahme der Anmeldeparameter aus der Adresse.
 *
 * Das Portal schickt den Benutzer mit `?user=…&token=<JWT>&tstoken=…` in das
 * Werkzeug. Diese Datei ist der EINZIGE Weg, auf dem ein Token in den Speicher
 * gelangt, und sie räumt die Adressleiste sofort wieder auf.
 *
 * ### Warum in `main.ts` und nicht in der Anmeldeseite
 *
 * Die Angular-Vorlage (Einschreiben-Werkzeug) übernimmt die Parameter in
 * `LoginPageComponent.ngOnInit`. Das trägt nur, solange das Portal immer auf
 * die Anmeldeseite zeigt. Für den Rücksprung auf eine Unterseite trägt es
 * nicht: Bei `…/upload-tool?user=…&token=…` greift zuerst der Routen-Wächter,
 * leitet auf `auth/login` um - und `Router.navigate` schneidet die
 * Suchparameter dabei ab. Die Token wären verloren, bevor die Anmeldeseite
 * überhaupt lädt. Genau daran scheiterte der Rücksprung bisher.
 *
 * Deshalb läuft die Übernahme vor `bootstrapModule`, also bevor der Router die
 * Adresse zum ersten Mal ansieht. Sie braucht dafür kein Angular: nur
 * `location`, `history` und `localStorage`.
 *
 * ### Zwei Feinheiten, die leicht kaputtgehen
 *
 * 1. `user` wird UNDEKODIERT übernommen. Der Wert ist eine verschlüsselte
 *    Kennung, die das Backend zweimal dekodiert
 *    (`getParameter` + `URLDecoder.decode`). Wer hier einmal dekodiert und den
 *    Wert später neu kodieren lässt, verschiebt die Zeichen und die Auflösung
 *    des Benutzers scheitert. Deshalb: roh lesen, roh weitergeben - genau wie
 *    bisher.
 * 2. Trifft eine neue Übergabe ein, wird der zwischengespeicherte Benutzer
 *    verworfen. Sonst sieht die nächste Person kurzzeitig Namen und Rollen der
 *    vorigen.
 */

const PARAM_USER = 'user'
const PARAM_JWT = 'token'
const PARAM_TS = 'tstoken'

const AUTH_PARAMS = [PARAM_USER, PARAM_JWT, PARAM_TS]

/**
 * Liest einen Suchparameter ROH, also ohne Prozent-Dekodierung.
 * `URLSearchParams.get()` dekodiert immer - für `user` ist das schädlich.
 */
function rawParam(search: string, name: string): string | null {
  const query = search.charAt(0) === '?' ? search.slice(1) : search
  for (const pair of query.split('&')) {
    if (!pair) {
      continue
    }
    const index = pair.indexOf('=')
    const key = index < 0 ? pair : pair.slice(0, index)
    if (key === name) {
      return index < 0 ? '' : pair.slice(index + 1)
    }
  }
  return null
}

/** Dekodiert wohlwollend: Bei kaputter Prozent-Folge bleibt der Rohwert. */
function decodeParam(value: string): string {
  try {
    return decodeURIComponent(value.replace(/\+/g, ' '))
  } catch {
    return value
  }
}

function write(key: string, value: string): void {
  try {
    localStorage.setItem(key, value)
  } catch {
    /* Speicher gesperrt (privates Fenster) - dann bleibt es beim 401. */
  }
}

function drop(key: string): void {
  try {
    localStorage.removeItem(key)
  } catch {
    /* siehe oben */
  }
}

/**
 * Hat dieser Seitenaufruf mit einer Portal-Übergabe begonnen?
 *
 * Wird beim Start EINMAL gesetzt und danach nur noch gelesen. Wichtig für
 * alles, was zwischen „der Benutzer sieht die Anmeldeseite an" und „der
 * Benutzer kommt gerade vom Portal herein" unterscheiden muss - die Adresse
 * allein verrät das nicht mehr, weil die Parameter sofort entfernt werden.
 */
let uebergabeErfolgt = false;

/** Kam dieser Aufruf über eine Portal-Übergabe? */
export function hatUebergabeStattgefunden(): boolean {
  return uebergabeErfolgt
}

/**
 * Übernimmt die Anmeldeparameter und entfernt sie aus der Adressleiste.
 *
 * @returns `true`, wenn eine Übergabe stattgefunden hat.
 */
export function bootstrapAuthFromUrl(): boolean {
  let search: string
  try {
    search = window.location.search || ''
  } catch {
    return false
  }
  if (!search) {
    return false
  }

  const rawUser = rawParam(search, PARAM_USER)
  const rawJwt = rawParam(search, PARAM_JWT)
  const rawTs = rawParam(search, PARAM_TS)

  if (rawUser === null && rawJwt === null && rawTs === null) {
    return false
  }

  uebergabeErfolgt = true

  // Eine Übergabe kann eine andere Person betreffen. Erst räumen, dann setzen.
  drop(AUTH_STORAGE_KEYS.user)

  if (rawUser) {
    write(AUTH_STORAGE_KEYS.userParam, rawUser)
  }
  if (rawJwt) {
    // JWT besteht aus base64url-Teilen; Dekodieren ist hier ein Nullvorgang,
    // schützt aber, falls das Portal den Wert je kodiert weitergibt.
    write(AUTH_STORAGE_KEYS.jwt, decodeParam(rawJwt))
  }
  if (rawTs) {
    // Muss dekodiert werden: Der Wert ist Base64 und kann `+` und `=`
    // enthalten, die in der Adresse als `%2B`/`%3D` ankommen. Undekodiert
    // scheitert später jedes `atob()`.
    write(AUTH_STORAGE_KEYS.tsToken, decodeParam(rawTs))
  }

  // Adressleiste aufräumen, fachliche Parameter behalten.
  try {
    const params = new URLSearchParams(search)
    AUTH_PARAMS.forEach((name) => params.delete(name))
    const rest = params.toString()
    const base = window.location.origin + window.location.pathname
    const hash = window.location.hash || ''
    window.history.replaceState(
      {},
      document.title,
      rest ? `${base}?${rest}${hash}` : `${base}${hash}`
    )
  } catch {
    /* `replaceState` nicht verfügbar - unkritisch, nur bleibt die Adresse stehen. */
  }

  return true
}
